/**
 * Shareable Verification Link Utilities V1
 * 
 * Deterministic encoding/decoding for shareable verification links.
 * Cryptographic signing for tamper-evident, identity-bound verification.
 * No execution, no mutation, no auth required.
 */

const MAX_PAYLOAD_SIZE_BYTES = 300 * 1024; // 300KB max payload

export interface ShareableBundlePayload {
  bundle: any;
  bundleHash: string;
  metadata?: {
    timestamp?: string;
    version?: string;
  };
}

export interface SignatureData {
  signature: string; // base64
  publicKey: string; // base64 SPKI
  identity: string; // human-readable (email/org/name)
}

export interface MultiSignatureEntry {
  id: string; // identity
  publicKey: string; // base64 SPKI
  signature: string; // base64
  fingerprint: string; // hex fingerprint
}

export interface MultiSignaturePayload {
  signatures: MultiSignatureEntry[];
  quorum?: {
    required: number;
  };
}

export type SignatureVerificationStatus = 'valid' | 'invalid' | 'missing' | 'malformed';

export interface VerifiedSignature {
  entry: MultiSignatureEntry;
  status: SignatureVerificationStatus;
}

export interface SignedShareablePayload extends ShareableBundlePayload {
  signature?: string;
  publicKey?: string;
  identity?: string;
}

/**
 * Deterministic JSON serialization with sorted keys.
 * Ensures same object always produces same string.
 */
function deterministicStringify(obj: any): string {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }
  
  if (Array.isArray(obj)) {
    return `[${obj.map(deterministicStringify).join(',')}]`;
  }
  
  const sortedKeys = Object.keys(obj).sort();
  const keyValuePairs = sortedKeys.map(key => {
    const value = obj[key];
    return `"${key}":${deterministicStringify(value)}`;
  });
  
  return `{${keyValuePairs.join(',')}}`;
}

/**
 * Encode bundle to base64 with fail-closed validation.
 * Returns null if encoding fails or payload too large.
 */
export function encodeShareableBundle(payload: ShareableBundlePayload): string | null {
  try {
    // Validate payload shape
    if (!payload || typeof payload !== 'object') {
      return null;
    }
    if (!payload.bundle || !payload.bundleHash) {
      return null;
    }
    
    // Deterministic serialization
    const jsonString = deterministicStringify(payload);
    
    // Size guard (DoS protection)
    const sizeBytes = new Blob([jsonString]).size;
    if (sizeBytes > MAX_PAYLOAD_SIZE_BYTES) {
      return null;
    }
    
    // Base64 encode
    const base64 = btoa(jsonString);
    return base64;
  } catch {
    // Fail closed on any error
    return null;
  }
}

/**
 * Decode base64 to bundle with fail-closed validation.
 * Returns null if decoding fails or invalid payload.
 */
export function decodeShareableBundle(base64: string): ShareableBundlePayload | null {
  try {
    // Validate input
    if (!base64 || typeof base64 !== 'string') {
      return null;
    }
    
    // Size guard before decode (base64 is ~33% larger than original)
    if (base64.length > MAX_PAYLOAD_SIZE_BYTES * 1.5) {
      return null;
    }
    
    // Base64 decode
    const jsonString = atob(base64);
    
    // Size guard after decode
    const sizeBytes = new Blob([jsonString]).size;
    if (sizeBytes > MAX_PAYLOAD_SIZE_BYTES) {
      return null;
    }
    
    // Parse JSON
    const parsed = JSON.parse(jsonString);
    
    // Validate payload shape
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (!parsed.bundle || !parsed.bundleHash) {
      return null;
    }
    
    return parsed;
  } catch {
    // Fail closed on any error
    return null;
  }
}

/**
 * Generate shareable URL from payload.
 * Returns null if encoding fails.
 */
export function generateShareableUrl(payload: ShareableBundlePayload, baseUrl: string = '/verify'): string | null {
  const encoded = encodeShareableBundle(payload);
  if (!encoded) {
    return null;
  }
  return `${baseUrl}?bundle=${encodeURIComponent(encoded)}`;
}

/**
 * Extract and decode bundle from URL search params.
 * Returns null if extraction or decoding fails.
 */
export function extractBundleFromUrl(searchParams: URLSearchParams): ShareableBundlePayload | null {
  const bundleParam = searchParams.get('bundle');
  if (!bundleParam) {
    return null;
  }
  return decodeShareableBundle(bundleParam);
}

/**
 * Extract signature data from URL search params.
 * Returns null if signature data missing or invalid.
 */
export function extractSignatureFromUrl(searchParams: URLSearchParams): SignatureData | null {
  const sig = searchParams.get('sig');
  const pub = searchParams.get('pub');
  const id = searchParams.get('id');

  if (!sig || !pub || !id) {
    return null;
  }

  // Validate base64 format
  try {
    atob(sig);
    atob(pub);
  } catch {
    return null;
  }

  return { signature: sig, publicKey: pub, identity: id };
}

/**
 * Generate ECDSA P-256 keypair for signing.
 * Returns null if crypto not available.
 */
export async function generateKeyPair(): Promise<CryptoKeyPair | null> {
  try {
    return await window.crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      },
      true,
      ['sign', 'verify']
    );
  } catch {
    return null;
  }
}

/**
 * Export public key as base64 SPKI format.
 * Returns null if export fails.
 */
export async function exportPublicKey(publicKey: CryptoKey): Promise<string | null> {
  try {
    const exported = await window.crypto.subtle.exportKey('spki', publicKey);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
    return base64;
  } catch {
    return null;
  }
}

/**
 * Import public key from base64 SPKI format.
 * Returns null if import fails.
 */
export async function importPublicKey(base64: string): Promise<CryptoKey | null> {
  try {
    const buffer = base64ToArrayBuffer(base64);
    return await window.crypto.subtle.importKey(
      'spki',
      buffer,
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      },
      true,
      ['verify']
    );
  } catch {
    return null;
  }
}

/**
 * Compute SHA-256 hash of data.
 * Returns null if hash fails.
 */
export async function sha256(data: string): Promise<Uint8Array | null> {
  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(data);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoded);
    return new Uint8Array(hashBuffer);
  } catch {
    return null;
  }
}

/**
 * Sign data with private key using ECDSA P-256.
 * Returns base64 signature or null on failure.
 */
export async function signData(privateKey: CryptoKey, data: string): Promise<string | null> {
  try {
    const hash = await sha256(data);
    if (!hash) return null;

    const signature = await window.crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: 'SHA-256'
      },
      privateKey,
      hash as BufferSource
    );

    const base64 = btoa(String.fromCharCode(...new Uint8Array(signature)));
    return base64;
  } catch {
    return null;
  }
}

/**
 * Verify signature with public key.
 * Returns true if valid, false otherwise.
 */
export async function verifySignature(
  publicKey: CryptoKey,
  signature: string,
  data: string
): Promise<boolean> {
  try {
    const hash = await sha256(data);
    if (!hash) return false;

    const binaryString = atob(signature);
    const sigBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      sigBytes[i] = binaryString.charCodeAt(i);
    }

    return await window.crypto.subtle.verify(
      {
        name: 'ECDSA',
        hash: 'SHA-256'
      },
      publicKey,
      sigBytes as BufferSource,
      hash as BufferSource
    );
  } catch {
    return false;
  }
}

/**
 * Generate key fingerprint from public key (SHA-256 of SPKI).
 * Returns hex string or null on failure.
 */
export async function generateKeyFingerprint(publicKey: CryptoKey): Promise<string | null> {
  try {
    const exported = await window.crypto.subtle.exportKey('spki', publicKey);
    const hash = await window.crypto.subtle.digest('SHA-256', exported);
    const hex = Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join(':');
    return hex;
  } catch {
    return null;
  }
}

/**
 * Get or generate cached keypair from localStorage.
 * Returns keypair or null on failure.
 */
export async function getOrGenerateKeyPair(): Promise<CryptoKeyPair | null> {
  const STORAGE_KEY = 'xable_verify_keypair';

  try {
    // Try to load from localStorage
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      if (data.privateKey && data.publicKey) {
        // Import keys
        const privateKeyBuffer = base64ToArrayBuffer(data.privateKey);
        const privateKey = await window.crypto.subtle.importKey(
          'pkcs8',
          privateKeyBuffer,
          {
            name: 'ECDSA',
            namedCurve: 'P-256'
          },
          true,
          ['sign']
        );
        const publicKeyBuffer = base64ToArrayBuffer(data.publicKey);
        const publicKey = await window.crypto.subtle.importKey(
          'spki',
          publicKeyBuffer,
          {
            name: 'ECDSA',
            namedCurve: 'P-256'
          },
          true,
          ['verify']
        );
        return { privateKey, publicKey };
      }
    }

    // Generate new keypair
    const keypair = await generateKeyPair();
    if (!keypair) return null;

    // Cache in localStorage
    const privateKeyExport = await window.crypto.subtle.exportKey('pkcs8', keypair.privateKey);
    const publicKeyExport = await window.crypto.subtle.exportKey('spki', keypair.publicKey);

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      privateKey: uint8ArrayToBase64(new Uint8Array(privateKeyExport)),
      publicKey: uint8ArrayToBase64(new Uint8Array(publicKeyExport))
    }));

    return keypair;
  } catch {
    return null;
  }
}

/**
 * Convert Uint8Array to base64 string.
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Convert base64 string to Uint8Array.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert base64 string to ArrayBuffer.
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const bytes = base64ToUint8Array(base64);
  return bytes.buffer.slice(0, bytes.byteLength) as ArrayBuffer;
}

/**
 * Encode multi-signature payload to base64.
 * Returns null if encoding fails or payload too large.
 */
export function encodeMultiSignaturePayload(payload: MultiSignaturePayload): string | null {
  try {
    const json = deterministicStringify(payload);
    if (json.length > MAX_PAYLOAD_SIZE_BYTES) {
      return null;
    }
    return btoa(json);
  } catch {
    return null;
  }
}

/**
 * Decode multi-signature payload from base64.
 * Returns null if decoding fails or payload malformed.
 */
export function decodeMultiSignaturePayload(encoded: string): MultiSignaturePayload | null {
  try {
    const decoded = atob(encoded);
    if (decoded.length > MAX_PAYLOAD_SIZE_BYTES) {
      return null;
    }
    const parsed = JSON.parse(decoded);
    
    // Validate structure
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (!Array.isArray(parsed.signatures)) {
      return null;
    }
    if (parsed.quorum !== undefined && typeof parsed.quorum !== 'object') {
      return null;
    }
    if (parsed.quorum && typeof parsed.quorum.required !== 'number') {
      return null;
    }
    
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Extract multi-signature payload from URL search params.
 * Returns null if extraction or decoding fails.
 */
export function extractMultiSignatureFromUrl(searchParams: URLSearchParams): MultiSignaturePayload | null {
  const sigsParam = searchParams.get('sigs');
  if (!sigsParam) {
    return null;
  }
  return decodeMultiSignaturePayload(sigsParam);
}

/**
 * Deduplicate signatures by fingerprint.
 * Returns array with unique fingerprints (first occurrence wins).
 */
export function deduplicateSignatures(signatures: MultiSignatureEntry[]): MultiSignatureEntry[] {
  const seen = new Set<string>();
  const result: MultiSignatureEntry[] = [];
  
  for (const sig of signatures) {
    if (!seen.has(sig.fingerprint)) {
      seen.add(sig.fingerprint);
      result.push(sig);
    }
  }
  
  return result;
}

/**
 * Verify multiple signatures against bundle data.
 * Returns array of verified signatures with status.
 */
export async function verifyMultiSignatures(
  payload: MultiSignaturePayload,
  bundleData: any
): Promise<VerifiedSignature[]> {
  const results: VerifiedSignature[] = [];
  const bundleJson = deterministicStringify(bundleData);
  
  for (const entry of payload.signatures) {
    try {
      const publicKey = await importPublicKey(entry.publicKey);
      if (!publicKey) {
        results.push({ entry, status: 'malformed' });
        continue;
      }
      
      const isValid = await verifySignature(publicKey, entry.signature, bundleJson);
      results.push({ entry, status: isValid ? 'valid' : 'invalid' });
    } catch {
      results.push({ entry, status: 'malformed' });
    }
  }
  
  return results;
}

/**
 * Calculate quorum status.
 * Returns true if valid signatures meet or exceed required count.
 */
export function calculateQuorumStatus(
  verifiedSignatures: VerifiedSignature[],
  required: number
): boolean {
  const validCount = verifiedSignatures.filter(s => s.status === 'valid').length;
  return validCount >= required;
}

/**
 * Add signature to multi-signature payload.
 * Deduplicates by fingerprint.
 */
export function addSignatureToPayload(
  payload: MultiSignaturePayload,
  newSignature: MultiSignatureEntry
): MultiSignaturePayload {
  const deduplicated = deduplicateSignatures([...payload.signatures, newSignature]);
  return {
    ...payload,
    signatures: deduplicated
  };
}
